import Modal from 'react-modal';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import { Side } from 'shared-types';
import styled from 'styled-components';

import Box from 'components/Box/Box';
import SContainer from 'components/Container/Container';
import FilePreview from 'components/ProductSide/FilePreview';
import ToolsBar from 'components/Tools/ToolsBar';

import { imagePlaces, scalingMethods } from 'utils/constants';

// Custom styled components for the editor layout
const EditorLayout = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
`;

const TopToolbar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 300px; /* Leave space for sidebar */
  height: 50px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.9) 0%,
    rgba(255, 255, 255, 0.7) 100%
  );
`;

const PreviewContainer = styled.div`
  position: absolute;
  top: 50px; /* Below toolbar */
  left: 0;
  right: 300px; /* Leave space for sidebar */
  bottom: 0;
  overflow: hidden; /* No scrollbars on this container */
  padding: 0;
`;

const PdfViewerWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: auto; /* Scrollbars only on this inner container */
  display: flex;
  justify-content: center;
  align-items: center;
  border: 2px solid red; /* For debugging - will be removed */
`;

const SidebarArea = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 300px;
  bottom: 0;
  background: #ffffff;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  z-index: 900;
  padding: 20px;
  overflow-y: auto;
`;

const CloseButton = styled.div`
  text-align: right;
  margin-bottom: 20px;
  cursor: pointer;
`;

Modal.setAppElement('#root');

const customStyles = {
  content: {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    padding: 0,
    border: 'none',
  },
};

type Props = {
  originalPdfFile: Blob;
  pdfFile: Blob | undefined;
  width: number;
  height: number;
  actions: Side['actions'];
  isOpen: boolean;
  onFlip: () => void;
  onRotate: (direction: 'left' | 'right') => void;
  onChangePlace: (place: string) => void;
  onChangeType: (type: string) => void;
  onClose: () => void;
};

function Editor({
  pdfFile,
  originalPdfFile,
  actions,
  onFlip,
  onRotate,
  onChangePlace,
  onChangeType,
  isOpen,
  onClose,
}: Props) {
  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={onClose}
        style={customStyles}
        contentLabel="File Editor"
      >
        <EditorLayout>
          {/* Top toolbar with controls */}
          <TopToolbar>
            {/* Placeholder for toolbar buttons that were in the screenshot */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <span role="img" aria-label="refresh">
                  ⟳
                </span>
              </button>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <span role="img" aria-label="download">
                  ↓
                </span>
              </button>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <span role="img" aria-label="print">
                  🖨️
                </span>
              </button>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <span role="img" aria-label="text">
                  T
                </span>
              </button>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <span role="img" aria-label="grid">
                  ⊞
                </span>
              </button>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <span role="img" aria-label="window">
                  ◧
                </span>
              </button>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <span role="img" aria-label="fullscreen">
                  ⛶
                </span>
              </button>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <span role="img" aria-label="menu">
                  ≡
                </span>
              </button>
            </div>
          </TopToolbar>

          {/* PDF Preview Container - below toolbar */}
          <PreviewContainer>
            {/* Inner container that will have scrollbars */}
            <PdfViewerWrapper>
              <FilePreview
                file={pdfFile || originalPdfFile}
                isFullPreview={true}
                loading={false}
                pageWidth={200}
              />
            </PdfViewerWrapper>
          </PreviewContainer>

          {/* Right side - Toolbox Area */}
          <SidebarArea>
            <CloseButton>
              <CloseOutlined onClick={onClose} />
            </CloseButton>

            <ToolsBar
              place={actions.place}
              type={actions.type}
              imagePlaces={imagePlaces}
              scalingMethods={scalingMethods}
              onChangePlace={onChangePlace}
              onRotateRight={() => onRotate('right')}
              onRotateLeft={() => onRotate('left')}
              onFlip={onFlip}
              onChangeType={onChangeType}
            />
          </SidebarArea>
        </EditorLayout>
      </Modal>
    </>
  );
}

export default Editor;
